// Topbar — CRM header strip.
//
// Layout: [workspace name] .......... [avatar+email] [logout].

import { useEffect, useState, type ReactElement } from 'react';
import './shell-alignment.css';
import { useNavigate } from 'react-router-dom';
import {
  Bell,
  Bot,
  LogOut,
  Menu,
  Monitor,
  Moon,
  PanelRightClose,
  PanelRightOpen,
  Plus,
  Search,
  Settings,
  Sun,
} from 'lucide-react';

import { Button, Dropdown, Input, Modal, Select } from 'antd';
import { useAnalytics } from '@govard/analytics';
import { useTranslation } from '@govard/i18n-core/react';

import { ApiError, apiPost } from '../../shared/api-client/index.js';
import {
  useAvailableWorkspaces,
  useCurrentScope,
  useCurrentUser,
  useScopeStore,
  useWorkspaceSwitching,
} from '../../shared/scope/useCurrentScope.js';
import { useTheme } from '../../theme/ThemeContext.js';
import { LanguageSwitcher } from './LanguageSwitcher.js';

interface TopbarProps {
  rightSidebarOpen?: boolean;
  onToggleRightSidebar?: () => void;
  /** True while the context toggle drives the docked AI cockpit (supported
   * route + `ai-cockpit` flag). On phones that control normally lives in the
   * `sm:`-only action group, but the cockpit collapses into a drawer there, so
   * closing it would leave no way back: this promotes the same control to a
   * phone-visible button. On every other route the legacy behavior is kept and
   * the toggle stays desktop-only. */
  mobileContextToggle?: boolean;
  systemAgentOpen?: boolean;
  onToggleSystemAgent?: () => void;
  onOpenCommandMenu?: () => void;
  onToggleMobileMenu?: () => void;
  /** True while the mobile nav drawer is open: the hamburger must not stay
   * rendered above the drawer. A still-visible toggle invites a second tap
   * that stacks overlays, and when the drawer panel covers the button it
   * becomes a dead zone anyway. Close affordances: in-drawer X, mask, Esc. */
  mobileMenuOpen?: boolean;
}

export function ThemeSwitcher(): ReactElement {
  const { t } = useTranslation();
  const { mode, actualTheme, setMode } = useTheme();

  const themeIcon =
    mode === 'auto' ? (
      <Monitor size={18} strokeWidth={1.5} />
    ) : actualTheme === 'dark' ? (
      <Moon size={18} strokeWidth={1.5} />
    ) : (
      <Sun size={18} strokeWidth={1.5} />
    );

  const items = [
    {
      key: 'light',
      label: (
        <div className="flex items-center gap-2">
          <Sun size={14} />
          <span>{t('crm.topbar.theme_light')}</span>
        </div>
      ),
      onClick: () => setMode('light'),
    },
    {
      key: 'dark',
      label: (
        <div className="flex items-center gap-2">
          <Moon size={14} />
          <span>{t('crm.topbar.theme_dark')}</span>
        </div>
      ),
      onClick: () => setMode('dark'),
    },
    {
      key: 'auto',
      label: (
        <div className="flex items-center gap-2">
          <Monitor size={14} />
          <span>{t('crm.topbar.theme_system')}</span>
        </div>
      ),
      onClick: () => setMode('auto'),
    },
  ];

  return (
    <Dropdown menu={{ items, selectedKeys: [mode] }} placement="bottomRight" trigger={['click']}>
      <Button
        type="text"
        icon={themeIcon}
        aria-label={t('crm.topbar.theme')}
        title={t('crm.topbar.theme')}
        data-testid="crm-topbar-theme-switcher"
        style={{ color: 'var(--ant-color-text-secondary)' }}
      />
    </Dropdown>
  );
}

export function Topbar({
  rightSidebarOpen = false,
  onToggleRightSidebar,
  mobileContextToggle = false,
  systemAgentOpen = false,
  onToggleSystemAgent,
  onOpenCommandMenu,
  onToggleMobileMenu,
  mobileMenuOpen = false,
}: TopbarProps): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const analytics = useAnalytics();

  const user = useCurrentUser();
  const scope = useCurrentScope();
  const clear = useScopeStore((state) => state.clear);
  const workspaces = useAvailableWorkspaces();
  const fetchWorkspaces = useScopeStore((state) => state.fetchWorkspaces);
  const switchWorkspace = useScopeStore((state) => state.switchWorkspace);
  const createWorkspace = useScopeStore((state) => state.createWorkspace);
  const switching = useWorkspaceSwitching();

  const [loggingOut, setLoggingOut] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [createName, setCreateName] = useState('');
  const [creating, setCreating] = useState(false);

  const userId = user?.id ?? null;
  useEffect(() => {
    if (userId === null) {
      return;
    }
    void fetchWorkspaces();
  }, [userId, fetchWorkspaces]);

  const onCreateWorkspace = async (): Promise<void> => {
    const name = createName.trim();
    if (name.length === 0) return;
    setCreating(true);
    setError(null);
    try {
      await createWorkspace(name);
      setCreateName('');
      setCreateOpen(false);
    } catch {
      setError(t('crm.topbar.workspace_create_error'));
    } finally {
      setCreating(false);
    }
  };

  const workspaceLabel =
    workspaces.find((workspace) => workspace.id === scope?.workspaceId)?.name ??
    scope?.workspaceId ??
    t('crm.topbar.workspace_fallback');
  const canSwitchWorkspace = workspaces.length > 1 && scope !== null;

  const onSwitchWorkspace = async (workspaceId: string): Promise<void> => {
    setError(null);
    try {
      await switchWorkspace(workspaceId);
    } catch {
      setError(t('crm.topbar.workspace_switch_error'));
    }
  };
  const userEmail = user?.email ?? '';
  const userInitial = userEmail.length > 0 ? userEmail[0]!.toUpperCase() : '?';

  const onLogout = async (): Promise<void> => {
    setLoggingOut(true);
    setError(null);
    try {
      await apiPost<unknown, Record<string, never>>('/auth/logout', {});
      analytics.reset();
      clear();
      navigate('/login', { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        analytics.reset();
        clear();
        navigate('/login', { replace: true });
        return;
      }
      setError(t('crm.topbar.logout_error'));
    } finally {
      setLoggingOut(false);
    }
  };

  return (
    <header
      className="crm-topbar relative z-10 flex shrink-0 items-center justify-between gap-2 border-b px-3 sm:px-5 lg:px-10"
      data-testid="crm-topbar"
      style={{
        height: '64px',
        backgroundColor: 'var(--ant-color-bg-container)',
        borderBottom: '1px solid var(--ant-color-border)',
      }}
    >
      <div className="flex items-center gap-2 min-w-0 flex-1 md:max-w-[420px]">
        {onToggleMobileMenu && !mobileMenuOpen ? (
          // Breakpoint hiding lives on this plain wrapper, not on the antd
          // Button: antd cssinjs and .crm-topbar .ant-btn (shell-alignment.css)
          // are unlayered and beat @layer utilities, so `md:hidden` directly
          // on <Button> never applied — the hamburger stayed visible on
          // tablet/desktop and stacked the mobile drawer over the rail.
          <div className="contents md:hidden">
            <Button
              type="text"
              icon={<Menu size={18} strokeWidth={1.5} />}
              onClick={onToggleMobileMenu}
              aria-label={t('crm.topbar.open_menu')}
              title={t('crm.topbar.open_menu')}
              data-testid="crm-topbar-mobile-toggle"
              className="shrink-0 crm-btn-press"
              style={{ color: 'var(--ant-color-text-secondary)' }}
            />
          </div>
        ) : null}

        {mobileContextToggle ? (
          // The same control as the desktop context toggle, promoted below `sm`
          // where the action group is hidden: the cockpit is a drawer on phones,
          // so without this a closed cockpit could never be reopened. Breakpoint
          // hiding stays on the wrapper for the reason documented on the
          // hamburger above (`sm:hidden` on the antd Button itself loses to
          // unlayered antd/`shell-alignment.css` rules).
          <div className="contents sm:hidden">
            <Button
              type="text"
              icon={
                rightSidebarOpen ? (
                  <PanelRightClose size={18} strokeWidth={1.5} />
                ) : (
                  <PanelRightOpen size={18} strokeWidth={1.5} />
                )
              }
              onClick={onToggleRightSidebar}
              aria-pressed={rightSidebarOpen}
              aria-label={t('crm.topbar.toggle_context')}
              title={t('crm.topbar.toggle_context')}
              data-testid="crm-topbar-toggle-context-mobile"
              className="shrink-0 crm-btn-press"
              style={{ color: 'var(--ant-color-text-secondary)' }}
            />
          </div>
        ) : null}

        <div className="hidden min-w-0 flex-1 md:block" style={{ maxWidth: 420 }}>
          <Input
            placeholder={t('crm.topbar.search_placeholder')}
            readOnly
            onClick={onOpenCommandMenu}
            prefix={
              <Search
                size={16}
                strokeWidth={1.5}
                style={{ color: 'var(--ant-color-text-tertiary)', marginRight: 4 }}
              />
            }
            suffix={
              <kbd
                style={{
                  fontSize: 10,
                  padding: '2px 6px',
                  background: 'var(--ant-color-bg-layout)',
                  borderRadius: 4,
                  color: 'var(--ant-color-text-secondary)',
                  border: '1px solid var(--ant-color-border)',
                }}
              >
                ⌘K
              </kbd>
            }
            style={{
              backgroundColor: 'var(--ant-color-bg-layout)',
              border: '1px solid var(--ant-color-border)',
              outline: 'none',
              boxShadow: 'none',
              fontSize: '13px',
              color: 'var(--ant-color-text)',
              cursor: 'pointer',
              transition: 'background-color 0.2s, border-color 0.2s',
            }}
            className="search-input-hover"
          />
        </div>
      </div>

      <div
        className="hidden items-center xl:flex"
        aria-label={t('crm.topbar.workspace_picker')}
        role="group"
        style={{ gap: 8, marginLeft: 16 }}
      >
        {canSwitchWorkspace ? (
          <Select
            size="small"
            variant="borderless"
            value={scope.workspaceId}
            loading={switching}
            disabled={switching}
            aria-label={t('crm.topbar.workspace_picker')}
            data-testid="crm-topbar-workspace-picker"
            popupMatchSelectWidth={false}
            onChange={(value: string) => {
              void onSwitchWorkspace(value);
            }}
            options={workspaces.map((ws) => ({
              value: ws.id,
              label: ws.isPersonal
                ? `${ws.name} · ${t('crm.topbar.workspace_personal_suffix')}`
                : ws.name,
            }))}
            popupRender={(menu) => (
              <div>
                {menu}
                <div
                  className="cursor-pointer border-t px-3 py-2 text-xs font-medium transition-colors"
                  style={{
                    borderColor: 'var(--ant-color-border)',
                    color: 'var(--ant-color-primary)',
                  }}
                  onClick={() => setCreateOpen(true)}
                  data-testid="crm-topbar-workspace-create"
                >
                  + {t('crm.topbar.workspace_create')}
                </div>
              </div>
            )}
            style={{ minWidth: 140 }}
          />
        ) : (
          <span className="text-xs font-medium ml-1">{workspaceLabel}</span>
        )}
        {scope !== null ? (
          <Button
            type="text"
            size="small"
            icon={<Plus size={14} strokeWidth={2} />}
            aria-label={t('crm.topbar.workspace_create')}
            title={t('crm.topbar.workspace_create')}
            data-testid="crm-topbar-workspace-create-button"
            onClick={() => setCreateOpen(true)}
            style={{ color: 'var(--ant-color-text-secondary)' }}
          />
        ) : null}
      </div>

      <div
        className="ml-auto flex min-w-0 items-center border-l pl-2 sm:pl-3 lg:pl-5"
        aria-label={t('crm.topbar.user_menu')}
        role="group"
        style={{
          gap: 8,
          borderColor: 'var(--ant-color-border)',
        }}
      >
        <ThemeSwitcher />
        <LanguageSwitcher />
        <div
          className="hidden sm:flex"
          data-testid="crm-topbar-secondary-actions"
          style={{ gap: 8 }}
        >
          <Button
            type="text"
            icon={
              rightSidebarOpen ? (
                <PanelRightClose size={18} strokeWidth={1.5} />
              ) : (
                <PanelRightOpen size={18} strokeWidth={1.5} />
              )
            }
            onClick={onToggleRightSidebar}
            aria-pressed={rightSidebarOpen}
            aria-label={t('crm.topbar.toggle_context')}
            title={t('crm.topbar.toggle_context')}
            data-testid="crm-topbar-toggle-context"
            style={{ color: 'var(--ant-color-text-secondary)' }}
          />
          <Button
            type="text"
            icon={<Bot size={18} strokeWidth={1.5} />}
            title={t('crm.topbar.system_agent')}
            aria-label={t('crm.topbar.system_agent')}
            aria-pressed={systemAgentOpen}
            data-testid="crm-topbar-system-agent"
            onClick={onToggleSystemAgent}
            style={{
              color: systemAgentOpen
                ? 'var(--ant-color-primary, #b45309)'
                : 'var(--ant-color-text-secondary, #57534e)',
              backgroundColor: systemAgentOpen
                ? 'var(--ant-color-primary-bg, #fffbeb)'
                : 'transparent',
            }}
          />
          <Button
            type="text"
            icon={<Bell size={18} strokeWidth={1.5} />}
            aria-label={t('crm.topbar.notifications')}
            title={t('crm.topbar.notifications')}
            style={{ color: 'var(--ant-color-text-secondary)' }}
          />
          <Button
            type="text"
            icon={<Settings size={18} strokeWidth={1.5} />}
            aria-label={t('crm.topbar.settings')}
            title={t('crm.topbar.settings')}
            onClick={() => navigate('/settings')}
            style={{ color: 'var(--ant-color-text-secondary)' }}
          />
        </div>

        <div className="flex items-center gap-2">
          <div className="hidden items-center gap-2 sm:flex" data-testid="crm-topbar-identity">
            <span
              aria-hidden="true"
              className="flex h-8 w-8 items-center justify-center rounded-full text-xs font-semibold"
              style={{
                backgroundColor: 'var(--ant-color-primary-bg, #fffbeb)',
                color: 'var(--ant-color-primary, #b45309)',
                border: '1px solid var(--ant-color-primary-border, #fbbf24)',
              }}
            >
              {userInitial}
            </span>
            <span
              className="hidden text-[13px] font-medium xl:inline"
              style={{ color: 'var(--ant-color-text, #292524)' }}
            >
              {userEmail}
            </span>
          </div>
          <Button
            onClick={() => {
              void onLogout();
            }}
            loading={loggingOut}
            data-testid="crm-topbar-logout"
            size="small"
            aria-label={t('crm.topbar.logout')}
            title={t('crm.topbar.logout')}
            style={{ fontSize: 13 }}
          >
            <span className="inline-flex sm:hidden" data-testid="crm-topbar-logout-mobile-icon">
              <LogOut aria-hidden="true" size={16} strokeWidth={1.5} />
            </span>
            <span className="hidden sm:inline">{t('crm.topbar.logout')}</span>
          </Button>
        </div>
      </div>

      {error !== null ? (
        <p
          role="alert"
          className="absolute left-1/2 top-full z-20 -translate-x-1/2 rounded-b px-3 py-1 text-xs"
          style={{
            backgroundColor: 'var(--ant-color-error-bg, #fef2f2)',
            color: 'var(--ant-color-error-text, #b91c1c)',
            border: '1px solid var(--ant-color-error-border, #fca5a5)',
          }}
        >
          {error}
        </p>
      ) : null}

      <Modal
        open={createOpen}
        title={t('crm.topbar.workspace_create')}
        okText={t('crm.topbar.workspace_create_ok')}
        cancelText={t('crm.common.cancel')}
        confirmLoading={creating}
        onOk={() => void onCreateWorkspace()}
        onCancel={() => {
          setCreateOpen(false);
          setCreateName('');
        }}
        destroyOnHidden
      >
        <label
          className="mb-1 block text-xs font-medium"
          style={{ color: 'var(--ant-color-text-secondary)' }}
        >
          {t('crm.topbar.workspace_create_name')}
        </label>
        <Input
          value={createName}
          onChange={(e) => setCreateName(e.target.value)}
          onPressEnter={() => void onCreateWorkspace()}
          placeholder={t('crm.topbar.workspace_create_name_placeholder')}
          maxLength={100}
          autoFocus
          data-testid="crm-topbar-workspace-create-name"
        />
      </Modal>
    </header>
  );
}
